import React from "react";
import ReactDOM from "react-dom";
import "./index.scss";
import App, { AnonymousApp } from "./App";
import reportWebVitals from "./reportWebVitals";
import { HashRouter } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import { getAuth0ProviderConfig, queryClient } from "./GeneralConfig";
import { AppProvider } from "./AppProviders";
import TagManager from "react-gtm-module-nonce-patch";
import { QueryClientProvider } from "react-query";
import { pdfjs } from "react-pdf";
import { BootstrapError } from "./BootstrapError";

// Set up PDF.js worker globally
// This worker runs PDF parsing/rendering in a separate thread to avoid blocking the UI
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GTM_ID || "",
  auth: process.env.REACT_APP_GTM_AUTH || "",
  preview: process.env.REACT_APP_GTM_PREVIEW || "",
};

// Some paths should be accessible without authentication
// we make special exceptions for these paths here
const PUBLIC_PATH_PATTERNS = [/^#\/legacy\/password-reset\/[^/]+$/];

function isPublicPath(hash: string): boolean {
  return PUBLIC_PATH_PATTERNS.some((pattern) => pattern.test(hash));
}

function bootstrapFailureMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

(async () => {
  const root = document.getElementById("root");
  if (!root) return;

  let failureMessage: string | null = null;
  let providerConfig: ReturnType<typeof getAuth0ProviderConfig> | undefined;

  try {
    providerConfig = getAuth0ProviderConfig(window);
  } catch (e) {
    failureMessage = bootstrapFailureMessage(e);
  }

  if (!failureMessage && !providerConfig) {
    failureMessage = "Application failed to initialize.";
  }

  if (failureMessage) {
    ReactDOM.render(
      <React.StrictMode>
        <BootstrapError message={failureMessage} />
      </React.StrictMode>,
      root
    );
    return;
  }

  if (!providerConfig) {
    return;
  }

  TagManager.initialize(tagManagerArgs);

  if (window.location.hash !== "") {
    localStorage.setItem("redirectHash", window.location.hash);
  }

  if (localStorage.getItem("redirectHash")) {
    providerConfig.redirectUri = `${
      window.location.origin
    }${localStorage.getItem("redirectHash")}`;
    localStorage.removeItem("redirectHash");
  }

  ReactDOM.render(
    <React.StrictMode>
      <Auth0Provider {...providerConfig} legacySameSiteCookie={false}>
        <HashRouter>
          <QueryClientProvider client={queryClient}>
            {isPublicPath(window.location.hash) ? (
              <AnonymousApp />
            ) : (
              <AppProvider>
                <App />
              </AppProvider>
            )}
          </QueryClientProvider>
        </HashRouter>
      </Auth0Provider>
    </React.StrictMode>,
    root
  );
})();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console-log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
